import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { MMI_2026_ASSUMPTIONS } from '@app/finance';
import { TAX_RESIDENCIES, type Assumptions, type AssumptionsInput } from '@app/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiErrorMessage } from '@/lib/api';
import { formatIsoDate, formatRatio } from '@/lib/format';
import { percentToRatio, ratioToPercent } from '@/lib/rate';
import { Field } from '@/features/simulations/Field';
import { TAX_RESIDENCY_LABELS } from '@/features/simulations/labels';
import { useCreateAssumptions } from './queries';

/** Saisie en pourcentage ; l'API attend un ratio décimal (`percentToRatio`). */
const formSchema = z.object({
  taxResidency: z.enum(TAX_RESIDENCIES),
  acquisitionYear: z.number().int().min(2000).max(2100),
  inflation: z.number().min(-50).max(50),
  housingPriceGrowth: z.number().min(-50).max(50),
  personalTaxRate: z.number().min(0).max(100),
  socialContributionRate: z.number().min(0).max(100),
  capitalGainsTaxRate: z.number().min(0).max(100),
  buildingDepreciationYears: z.number().int().min(1).max(100),
  furnitureDepreciationYears: z.number().int().min(1).max(100),
  landShare: z.number().min(0).max(100),
});

type FormValues = z.infer<typeof formSchema>;

/** Valeurs du classeur MMI, pour une première saisie quand aucune version n'existe encore. */
export const BASELINE_ASSUMPTIONS: AssumptionsInput = {
  taxResidency: 'non-resident-eea',
  acquisitionYear: MMI_2026_ASSUMPTIONS.acquisitionYear,
  inflation: MMI_2026_ASSUMPTIONS.inflation,
  housingPriceGrowth: MMI_2026_ASSUMPTIONS.housingPriceGrowth,
  personalTaxRate: MMI_2026_ASSUMPTIONS.personalTaxRate,
  socialContributionRate: MMI_2026_ASSUMPTIONS.socialContributionRate,
  capitalGainsTaxRate: MMI_2026_ASSUMPTIONS.capitalGainsTaxRate,
  buildingDepreciationYears: MMI_2026_ASSUMPTIONS.buildingDepreciationYears,
  furnitureDepreciationYears: MMI_2026_ASSUMPTIONS.furnitureDepreciationYears,
  landShare: MMI_2026_ASSUMPTIONS.landShare,
};

function toFormValues(input: AssumptionsInput): FormValues {
  return {
    taxResidency: input.taxResidency,
    acquisitionYear: input.acquisitionYear,
    inflation: ratioToPercent(input.inflation),
    housingPriceGrowth: ratioToPercent(input.housingPriceGrowth),
    personalTaxRate: ratioToPercent(input.personalTaxRate),
    socialContributionRate: ratioToPercent(input.socialContributionRate),
    capitalGainsTaxRate: ratioToPercent(input.capitalGainsTaxRate),
    buildingDepreciationYears: input.buildingDepreciationYears,
    furnitureDepreciationYears: input.furnitureDepreciationYears,
    landShare: ratioToPercent(input.landShare),
  };
}

function toInput(values: FormValues): AssumptionsInput {
  return {
    taxResidency: values.taxResidency,
    acquisitionYear: values.acquisitionYear,
    inflation: percentToRatio(values.inflation),
    housingPriceGrowth: percentToRatio(values.housingPriceGrowth),
    personalTaxRate: percentToRatio(values.personalTaxRate),
    socialContributionRate: percentToRatio(values.socialContributionRate),
    capitalGainsTaxRate: percentToRatio(values.capitalGainsTaxRate),
    buildingDepreciationYears: values.buildingDepreciationYears,
    furnitureDepreciationYears: values.furnitureDepreciationYears,
    landShare: percentToRatio(values.landShare),
  };
}

interface Props {
  /** Version en vigueur ; `null` avant la toute première saisie. */
  current: Assumptions | null;
  /** Reprise d'une valeur mesurée ailleurs (calibrage DVF) : remplace la croissance des prix affichée. */
  suggestedGrowth: number | undefined;
}

export function AssumptionsForm({ current, suggestedGrowth }: Props) {
  const base: AssumptionsInput = current ?? BASELINE_ASSUMPTIONS;
  const initial = toFormValues(
    suggestedGrowth === undefined ? base : { ...base, housingPriceGrowth: suggestedGrowth },
  );
  const { register, control, handleSubmit, formState } = useForm<FormValues>({ defaultValues: initial });
  const createAssumptions = useCreateAssumptions();
  const [error, setError] = useState<string | null>(null);

  const onSubmit = handleSubmit((raw) => {
    const parsed = formSchema.safeParse(raw);
    if (!parsed.success) {
      setError('Corrigez les champs invalides avant d’enregistrer.');
      return;
    }
    setError(null);
    createAssumptions.mutate(toInput(parsed.data), {
      onSuccess: () => toast.success('Nouvelle version des hypothèses enregistrée'),
      onError: (failure) => setError(apiErrorMessage(failure, 'Échec de l’enregistrement')),
    });
  });

  const number = (name: keyof FormValues) => register(name, { valueAsNumber: true });

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
      <Alert>
        <AlertDescription>
          Enregistrer ajoute une <strong>nouvelle version</strong> ; les précédentes sont conservées mais ne sont
          plus lues. Toutes les projections, y compris celles déjà enregistrées, sont recalculées avec la version en
          vigueur.
          {current !== null && (
            <>
              {' '}Version actuelle du {formatIsoDate(current.createdAt.slice(0, 10))} : croissance des prix{' '}
              {formatRatio(current.housingPriceGrowth)}, inflation {formatRatio(current.inflation)}.
            </>
          )}
        </AlertDescription>
      </Alert>
      {suggestedGrowth !== undefined && (
        <p className="text-sm text-muted-foreground">
          Croissance des prix pré-remplie à {formatRatio(suggestedGrowth)} d’après le calibrage DVF. Rien n’est
          enregistré tant que vous ne validez pas.
        </p>
      )}

      <Field label="Résidence fiscale" htmlFor="taxResidency">
        <Controller
          control={control}
          name="taxResidency"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger className="w-full max-w-md" id="taxResidency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TAX_RESIDENCIES.map((residency) => (
                  <SelectItem key={residency} value={residency}>
                    {TAX_RESIDENCY_LABELS[residency]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </Field>

      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <legend className="col-span-full text-sm font-medium text-muted-foreground">Marché</legend>
        <Field label="Année d'acquisition" htmlFor="acquisitionYear">
          <Input id="acquisitionYear" type="number" step="1" {...number('acquisitionYear')} />
        </Field>
        <Field label="Inflation annuelle (%)" htmlFor="inflation">
          <Input id="inflation" type="number" step="0.01" {...number('inflation')} />
        </Field>
        <Field label="Croissance annuelle des prix du logement (%)" htmlFor="housingPriceGrowth">
          <Input id="housingPriceGrowth" type="number" step="0.01" {...number('housingPriceGrowth')} />
        </Field>
      </fieldset>

      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <legend className="col-span-full text-sm font-medium text-muted-foreground">Fiscalité</legend>
        <Field label="Taux d'imposition personnel (%)" htmlFor="personalTaxRate">
          <Input id="personalTaxRate" type="number" step="0.1" {...number('personalTaxRate')} />
        </Field>
        <Field label="Prélèvements sociaux (%)" htmlFor="socialContributionRate">
          <Input id="socialContributionRate" type="number" step="0.1" {...number('socialContributionRate')} />
        </Field>
        <Field label="Impôt sur la plus-value (%)" htmlFor="capitalGainsTaxRate">
          <Input id="capitalGainsTaxRate" type="number" step="0.001" {...number('capitalGainsTaxRate')} />
        </Field>
      </fieldset>

      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <legend className="col-span-full text-sm font-medium text-muted-foreground">Amortissement</legend>
        <Field label="Durée du bâti (années)" htmlFor="buildingDepreciationYears">
          <Input id="buildingDepreciationYears" type="number" step="1" min="1" {...number('buildingDepreciationYears')} />
        </Field>
        <Field label="Durée du mobilier (années)" htmlFor="furnitureDepreciationYears">
          <Input id="furnitureDepreciationYears" type="number" step="1" min="1" {...number('furnitureDepreciationYears')} />
        </Field>
        <Field label="Part du terrain (%)" htmlFor="landShare">
          <Input id="landShare" type="number" step="0.1" {...number('landShare')} />
        </Field>
      </fieldset>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}
      <div>
        <Button type="submit" disabled={createAssumptions.isPending || formState.isSubmitting}>
          {createAssumptions.isPending ? 'Enregistrement…' : 'Enregistrer une nouvelle version'}
        </Button>
      </div>
    </form>
  );
}
